import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { BRAND_COLORS, BRAND_NAME, BRAND_AI_NAME } from "@/lib/brand";
import { PupplMark } from "@/pages/Landing";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Signup() {
  const navigate = useNavigate();
  const [form, setForm] = useState({
    business_name: "",
    owner_first_name: "",
    owner_last_name: "",
    email: "",
    password: "",
  });
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  const set = (k) => (e) => {
    setForm((f) => ({ ...f, [k]: e.target.value }));
  };

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (form.password.length < 8) {
      setErr("Password must be at least 8 characters.");
      return;
    }
    setBusy(true);
    try {
      const r = await axios.post(`${API}/signup`, form);
      localStorage.setItem("pp_token", r.data.token);
      localStorage.setItem("pp_user", JSON.stringify(r.data.user));
      localStorage.setItem("puppl_tenant", JSON.stringify(r.data.tenant));
      // Hard redirect into the admin app so the AuthContext re-reads localStorage
      window.location.href = "/admin";
    } catch (e2) {
      const detail = e2?.response?.data?.detail;
      const msg = Array.isArray(detail)
        ? detail.map((d) => d.msg || d).join(", ")
        : detail || e2?.message || "Sign up failed";
      setErr(msg);
      setBusy(false);
    }
  };

  return (
    <div
      data-testid="puppl-signup-page"
      style={{
        minHeight: "100vh",
        background:
          "radial-gradient(70% 60% at 110% -10%, #D8F1E5 0%, transparent 50%)," +
          BRAND_COLORS.cream,
        color: BRAND_COLORS.ink,
        fontFamily: "'DM Sans', system-ui, sans-serif",
        display: "grid",
        placeItems: "center",
        padding: "32px 24px",
      }}
    >
      <main
        style={{
          width: "min(560px, 100%)",
          background: BRAND_COLORS.paper,
          border: `1px solid ${BRAND_COLORS.border}`,
          borderRadius: 24,
          padding: "40px 36px",
          boxShadow: "0 30px 60px -40px rgba(15,22,20,0.18)",
        }}
      >
        <Link
          to="/"
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 10,
            textDecoration: "none",
            color: BRAND_COLORS.ink,
            marginBottom: 22,
          }}
        >
          <PupplMark size={32} />
          <span style={{ fontFamily: "'Fraunces', serif", fontWeight: 700, fontSize: 22 }}>{BRAND_NAME}</span>
        </Link>
        <h1
          style={{
            fontFamily: "'Fraunces', serif",
            fontSize: 32,
            margin: 0,
            fontWeight: 600,
            letterSpacing: "-0.5px",
            lineHeight: 1.15,
          }}
        >
          Set up your training business
        </h1>
        <p style={{ marginTop: 8, marginBottom: 28, color: BRAND_COLORS.muted, lineHeight: 1.55 }}>
          90 seconds. {BRAND_AI_NAME} will be waiting for you on the other side.
        </p>

        <form onSubmit={submit} style={{ display: "grid", gap: 14 }}>
          <Field
            label="Business name"
            testid="signup-business-name"
            value={form.business_name}
            onChange={set("business_name")}
            placeholder="e.g. Coastal Canine Co."
            required
          />
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
            <Field
              label="Your first name"
              testid="signup-first-name"
              value={form.owner_first_name}
              onChange={set("owner_first_name")}
              required
            />
            <Field
              label="Last name"
              testid="signup-last-name"
              value={form.owner_last_name}
              onChange={set("owner_last_name")}
            />
          </div>
          <Field
            label="Email"
            testid="signup-email"
            type="email"
            value={form.email}
            onChange={set("email")}
            required
          />
          <Field
            label="Choose a password"
            testid="signup-password"
            type="password"
            value={form.password}
            onChange={set("password")}
            required
            help="At least 8 characters."
          />
          <div
            data-testid="signup-built-by-trainer"
            style={{
              display: "flex",
              gap: 12,
              alignItems: "flex-start",
              background: BRAND_COLORS.tealSoft,
              padding: "14px 16px",
              borderRadius: 14,
              fontSize: 14,
              lineHeight: 1.55,
              color: BRAND_COLORS.tealDeep,
            }}
          >
            <span aria-hidden style={{ fontSize: 22, lineHeight: 1 }}>🇦🇺</span>
            <span>
              <strong>Built in Australia, by a dog trainer for dog trainers.</strong>
              <br />
              You&apos;re joining trainers who&apos;ve all signed up to one promise: software that gets out of your way so you can do the work.
            </span>
          </div>
          {err && (
            <div
              data-testid="signup-error"
              style={{
                background: "#FFE5E5",
                color: "#8B1F1F",
                padding: "12px 14px",
                borderRadius: 12,
                fontSize: 14,
              }}
            >
              {err}
            </div>
          )}
          <button
            type="submit"
            data-testid="signup-submit"
            disabled={busy}
            style={{
              marginTop: 8,
              background: busy ? "#9BCFB8" : BRAND_COLORS.teal,
              color: "#fff",
              border: "none",
              padding: "16px 24px",
              borderRadius: 999,
              fontWeight: 600,
              fontSize: 16,
              cursor: busy ? "default" : "pointer",
              boxShadow: "0 8px 22px -10px rgba(51,192,149,0.6)",
              transition: "transform 180ms ease, background 180ms ease",
            }}
            onMouseEnter={(e) => {
              if (!busy) {
                e.currentTarget.style.transform = "translateY(-1px)";
                e.currentTarget.style.background = BRAND_COLORS.tealDeep;
              }
            }}
            onMouseLeave={(e) => {
              if (!busy) {
                e.currentTarget.style.transform = "translateY(0)";
                e.currentTarget.style.background = BRAND_COLORS.teal;
              }
            }}
          >
            {busy ? "Setting things up…" : "Create my Puppl account →"}
          </button>
        </form>
        <p style={{ marginTop: 22, fontSize: 14, color: BRAND_COLORS.muted }}>
          Already have an account?{" "}
          <Link to="/login" data-testid="signup-to-login" style={{ color: BRAND_COLORS.tealDeep, fontWeight: 600 }}>
            Sign in
          </Link>
        </p>
      </main>
    </div>
  );
}

function Field({ label, testid, help, ...inputProps }) {
  return (
    <label style={{ display: "grid", gap: 6, fontSize: 14, fontWeight: 500, color: BRAND_COLORS.ink }}>
      <span>{label}</span>
      <input
        data-testid={testid}
        {...inputProps}
        style={{
          padding: "13px 14px",
          borderRadius: 12,
          border: `1.5px solid ${BRAND_COLORS.border}`,
          background: BRAND_COLORS.cream,
          fontSize: 15,
          color: BRAND_COLORS.ink,
          outline: "none",
          transition: "border-color 160ms ease, box-shadow 160ms ease",
          fontFamily: "inherit",
        }}
        onFocus={(e) => {
          e.target.style.borderColor = BRAND_COLORS.teal;
          e.target.style.boxShadow = `0 0 0 3px ${BRAND_COLORS.teal}25`;
        }}
        onBlur={(e) => {
          e.target.style.borderColor = BRAND_COLORS.border;
          e.target.style.boxShadow = "none";
        }}
      />
      {help && <small style={{ fontWeight: 400, color: BRAND_COLORS.muted, fontSize: 13 }}>{help}</small>}
    </label>
  );
}
